import { useTranslation } from "react-i18next";
import ChatListItem from "./ChatListItem";

const ChatSidebar = ({
    query,
    setQuery,
    activeFilter,
    setActiveFilter,
    isLoadingChats,
    visiblePinned,
    recentChats,
    activeChat,
    menuChatId,
    sessionHasNext,
    mobileSidebarOpen,
    isMobile,
    onCloseMobile,
    onNewChat,
    onSelectChat,
    onToggleMenu,
    onTogglePin,
    onRequestDelete,
    onLoadMore,
    setIsResizing,
}) => {
    const { t } = useTranslation();
    return (
        <aside className={
            isMobile
                ? `fixed inset-y-0 left-0 z-[60] w-72 flex flex-col overflow-hidden
                   border-r border-white/70 bg-white/95 backdrop-blur-xl
                   transition-transform duration-300 ease-in-out
                   ${mobileSidebarOpen ? "translate-x-0" : "-translate-x-full"}`
                : "border-r border-white/70 bg-white/40 backdrop-blur-xl relative h-full min-h-0 flex flex-col"
        }>
            {/* Mobile header */}
            {isMobile && (
                <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-gray-100">
                    <div>
                        <h2 className="text-base font-semibold text-slate-900">{t('aura title')}</h2>
                        <p className="text-xs text-slate-500">{t('aura tagline')}</p>
                    </div>
                    <button
                        type="button"
                        aria-label="Close sidebar"
                        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-gray-100"
                        onClick={onCloseMobile}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            )}

            <div className="p-4 space-y-3">
                <button
                    type="button"
                    className="w-full rounded-[18px] bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 text-white text-sm font-semibold py-2.5
                    shadow-[0_20px_50px_rgba(99,102,241,0.35)] hover:opacity-95"
                    onClick={onNewChat}
                >
                    {t('aura sidebar new chat')}
                </button>

                <div className="rounded-[18px] border border-white/70 bg-white/65 backdrop-blur px-3 py-2
                shadow-[0_16px_40px_rgba(99,102,241,0.14)]">
                    <input
                        type="text"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder={t('aura sidebar search')}
                        className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
                    />
                </div>

                <div className="flex flex-wrap gap-2 text-[11px]">
                    {[
                        { value: "all", label: t('aura sidebar filter all') },
                        { value: "today", label: t('aura sidebar filter today') },
                        { value: "week", label: t('aura sidebar filter week') },
                        { value: "month", label: t('aura sidebar filter month') },
                    ].map(({ value, label }) => (
                        <button
                            key={value}
                            type="button"
                            onClick={() => setActiveFilter(value)}
                            className={`rounded-full px-4 py-1.5 border border-white/60 backdrop-blur transition
                            ${
                                activeFilter === value
                                    ? "bg-white/75 text-indigo-700 shadow-[0_12px_28px_rgba(15,23,42,0.10)]"
                                    : "bg-white/40 text-slate-600 hover:bg-white/60"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                <div className="text-[11px] text-slate-500">{t('aura sidebar live sessions')}</div>
            </div>

            <div className="px-3 pb-4 space-y-3 overflow-y-auto flex-1 min-h-0">
                {isLoadingChats ? (
                    <div className="space-y-2">
                        {Array.from({ length: 6 }).map((_, idx) => (
                            <div
                                key={`s-${idx}`}
                                className="h-14 rounded-[18px] bg-white/35 border border-white/40 animate-pulse"
                            />
                        ))}
                    </div>
                ) : (
                    <>
                        {visiblePinned.length > 0 && (
                            <div>
                                <div className="text-[11px] uppercase text-slate-500 mb-2 px-1">
                                    {t('aura sidebar pinned')}
                                </div>
                                <div className="space-y-2">
                                    {visiblePinned.map((chat) => (
                                        <ChatListItem
                                            key={chat.id}
                                            chat={chat}
                                            isActive={chat.id === activeChat?.id}
                                            menuChatId={menuChatId}
                                            onSelect={onSelectChat}
                                            onToggleMenu={onToggleMenu}
                                            onTogglePin={onTogglePin}
                                            onRequestDelete={onRequestDelete}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        <div>
                            <div className="text-[11px] uppercase text-slate-500 mb-2 px-1">
                                {t('aura sidebar recent')}
                            </div>
                            <div className="space-y-2">
                                {recentChats.map((chat) => (
                                    <ChatListItem
                                        key={chat.id}
                                        chat={chat}
                                        isActive={chat.id === activeChat?.id}
                                        menuChatId={menuChatId}
                                        onSelect={onSelectChat}
                                        onToggleMenu={onToggleMenu}
                                        onTogglePin={onTogglePin}
                                        onRequestDelete={onRequestDelete}
                                    />
                                ))}
                            </div>
                        </div>

                        {sessionHasNext && (
                            <button
                                type="button"
                                className="w-full rounded-[18px] border border-white/60 bg-white/55 px-4 py-3 text-xs text-slate-600
                                shadow-[0_14px_34px_rgba(15,23,42,0.08)] hover:bg-white/70"
                                onClick={onLoadMore}
                                disabled={isLoadingChats}
                            >
                                {isLoadingChats ? t('aura loading') : t('aura sidebar load more')}
                            </button>
                        )}
                    </>
                )}
            </div>

            {/* Resize handle */}
            <div
                className="absolute top-0 right-0 h-full w-1 cursor-col-resize bg-transparent"
                onMouseDown={() => setIsResizing(true)}
            />
        </aside>
    );
};

export default ChatSidebar;
